import { passwordSchema, VAULT_NAME_MAX, VAULT_NAME_RULE, vaultNameSchema, vaultSlug, type AccountDeletion, type DeviceInfo, type WorkspaceDataDeleted } from '@nebueye/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { AtSign, Copy, Inbox, Laptop, Loader2, MonitorSmartphone, ShieldCheck, Store, Trash2, UserX } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { DiscordIcon, Field, FormAlert } from '@/components/app/form'
import { PageHeader } from '@/components/app/states'
import { initials } from '@/components/app/user-menu'
import { WorkspaceInitial } from '@/components/app/workspace-switcher'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { authClient, authErrorMessage } from '@/lib/auth-client'
import { useVaultAvailability } from '@/lib/vault'
import { apiFetch } from '@/lib/api'
import { ago, plural } from '@/lib/format'
import { sessionQuery, workspacesQuery } from '@/lib/session'

const TABS = ['profile', 'security', 'vault', 'members', 'integrations'] as const
type Tab = (typeof TABS)[number]

export const Route = createFileRoute('/_app/settings')({
  validateSearch: z.object({ tab: z.enum(TABS).optional(), create: z.boolean().optional() }),
  head: () => ({ meta: [{ title: 'Settings · Nebueye' }] }),
  component: Settings,
})

function Settings() {
  const { tab = 'profile' } = Route.useSearch()
  const navigate = Route.useNavigate()
  return (
    <>
      <PageHeader title="Settings" />
      <Tabs value={tab} onValueChange={(t) => navigate({ search: { tab: t as Tab }, replace: true })} className="gap-8">
        <TabsList variant="line" className="h-10 w-full justify-start gap-2 overflow-x-auto rounded-none border-b p-0 [scrollbar-width:none]">
          {TABS.map((t) => (
            <TabsTrigger
              key={t}
              value={t}
              className="flex-none px-2 capitalize after:bg-primary"
            >
              {t}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="profile"><Profile /></TabsContent>
        <TabsContent value="security"><Security /></TabsContent>
        <TabsContent value="vault"><Workspace /></TabsContent>
        <TabsContent value="members"><Members /></TabsContent>
        <TabsContent value="integrations"><Integrations /></TabsContent>
      </Tabs>
    </>
  )
}

/** Two-column settings block: what it is on the left, the controls on the right. */
function Block({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <section className="grid gap-x-12 gap-y-4 border-b py-8 first:pt-0 last:border-0 md:grid-cols-[240px_minmax(0,1fr)]">
      <div className="space-y-1">
        <h2 className="text-[15px] font-semibold">{title}</h2>
        {description && <p className="text-muted-foreground text-[13px] leading-5">{description}</p>}
      </div>
      <div className="max-w-xl">{children}</div>
    </section>
  )
}

// ----------------------------------------------------------------- profile

function Profile() {
  const user = useQuery(sessionQuery).data?.user
  const queryClient = useQueryClient()
  const [name, setName] = useState(user?.name ?? '')
  const save = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.updateUser({ name: name.trim() })
      if (error) throw new Error(authErrorMessage(error))
    },
    onSuccess: () => {
      toast.success('Profile updated')
      queryClient.invalidateQueries({ queryKey: ['session'] })
    },
    onError: (e) => toast.error(e.message),
  })
  if (!user) return null
  return (
    <div>
      <Block title="Your profile" description="How you appear to people in your Vaults.">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
          className="space-y-4"
        >
          <div className="flex items-center gap-3">
            <Avatar className="size-12">
              {user.image && <AvatarImage src={user.image} alt="" />}
              <AvatarFallback className="bg-accent text-accent-foreground font-semibold">{initials(user.name)}</AvatarFallback>
            </Avatar>
            <div className="text-sm">
              <p className="font-medium">{user.email}</p>
              <p className="text-muted-foreground text-[13px]">{user.emailVerified ? 'Verified email' : 'Email not verified'}</p>
            </div>
          </div>
          <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          <Button type="submit" disabled={save.isPending || !name.trim() || name.trim() === user.name}>
            {save.isPending && <Loader2 className="animate-spin" />}
            Save
          </Button>
        </form>
      </Block>
      <DeleteAccount email={user.email} />
    </div>
  )
}

/**
 * Apple requires an in-app way to delete the account; everyone deserves one.
 * Deletion is scheduled 30 days out: other devices are signed out now, and
 * signing in before then and choosing "Keep my account" cancels it.
 */
function DeleteAccount({ email }: { email: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const matches = typed.trim().toLowerCase() === email.trim().toLowerCase()
  const plan = useQuery({ queryKey: ['account-deletion'], queryFn: () => apiFetch<AccountDeletion>('/account/deletion'), enabled: open })
  const close = () => {
    setOpen(false)
    setTyped('')
  }
  const request = useMutation({
    mutationFn: () =>
      apiFetch<AccountDeletion>('/account/delete', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ confirm: typed }) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      navigate({ to: '/account-deletion' })
    },
    onError: (e) => toast.error(e.message),
  })
  const names = (ws: { name: string }[]) => ws.map((w) => w.name).join(', ')

  return (
    <Block title="Delete account" description="Deletes your account and everything only you had access to, 30 days from now. You can change your mind until then.">
      {!open ? (
        <Button variant="outline" className="text-status-danger-text" onClick={() => setOpen(true)}>
          <UserX />
          Delete my account…
        </Button>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (matches) request.mutate()
          }}
        >
          {plan.data && (
            <ul className="text-muted-foreground space-y-1 text-sm">
              {plan.data.workspacesToDelete.length > 0 && (
                <li>
                  <span className="text-foreground font-medium">Deleted with all their orders:</span> {names(plan.data.workspacesToDelete)}
                </li>
              )}
              {plan.data.workspacesToLeave.length > 0 && (
                <li>
                  <span className="text-foreground font-medium">You leave, the others keep their data:</span> {names(plan.data.workspacesToLeave)}
                </li>
              )}
              <li>Your other devices are signed out now and desktops disconnected.</li>
            </ul>
          )}
          <Field
            label={`Type “${email}” to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            hint="Your account is deleted in 30 days. Sign in before then to keep it."
          />
          <div className="flex gap-2">
            <Button type="submit" variant="destructive" disabled={!matches || request.isPending}>
              {request.isPending && <Loader2 className="animate-spin" />}
              Delete my account
            </Button>
            <Button type="button" variant="ghost" onClick={close} disabled={request.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Block>
  )
}

// ---------------------------------------------------------------- security

function Security() {
  return (
    <div>
      <PasswordBlock />
      <TwoFactorBlock />
      <LinkedAccounts />
      <Sessions />
    </div>
  )
}

/**
 * Authenticator-app two-factor. Setup is two steps: the password unlocks the
 * secret (QR code + backup codes), then one code from the app proves it was
 * added. Only then is it on. Turning it off, or replacing the backup codes,
 * asks for the password again.
 */
function TwoFactorBlock() {
  const queryClient = useQueryClient()
  const session = useQuery(sessionQuery).data
  const enabled = !!(session?.user as { twoFactorEnabled?: boolean | null } | undefined)?.twoFactorEnabled
  const [setup, setSetup] = useState<{ totpURI: string; backupCodes: string[] }>()
  const [codes, setCodes] = useState<string[]>()
  const [pending, setPending] = useState<'start' | 'confirm' | 'disable' | 'codes'>()
  const [error, setError] = useState<string>()

  const run = async <T,>(step: NonNullable<typeof pending>, fn: () => Promise<{ data: T | null; error: { code?: string; message?: string; status?: number } | null }>) => {
    setPending(step)
    setError(undefined)
    const { data, error } = await fn()
    setPending(undefined)
    if (error) {
      setError(authErrorMessage(error))
      return null
    }
    return data
  }
  const password = (form: HTMLFormElement) => String(new FormData(form).get('password'))

  if (setup) {
    const secret = new URL(setup.totpURI).searchParams.get('secret') ?? ''
    return (
      <Block title="Two-factor authentication" description="Scan the code with an authenticator app (1Password, Google Authenticator, Authy…), save the backup codes, then enter the code the app shows.">
        <div className="space-y-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="rounded-lg border bg-white p-3">
              <QRCodeSVG value={setup.totpURI} size={160} marginSize={0} />
            </div>
            <div className="min-w-0 space-y-1.5 text-[13px]">
              <p className="text-muted-foreground">Can't scan? Enter this key by hand:</p>
              <p className="bg-muted rounded-md px-2.5 py-1.5 font-mono text-xs break-all">{secret}</p>
            </div>
          </div>
          <BackupCodes codes={setup.backupCodes} />
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault()
              const code = String(new FormData(e.currentTarget).get('code')).replace(/\s+/g, '')
              const ok = await run('confirm', () => authClient.twoFactor.verifyTotp({ code }))
              if (!ok) return
              setSetup(undefined)
              await queryClient.invalidateQueries({ queryKey: ['session'] })
              toast.success('Two-factor authentication is on.')
            }}
          >
            {error && <FormAlert>{error}</FormAlert>}
            <Field label="Code from the app" name="code" inputMode="numeric" autoComplete="one-time-code" className="font-mono" required autoFocus />
            <div className="flex gap-2">
              <Button type="submit" disabled={pending === 'confirm'}>
                {pending === 'confirm' && <Loader2 className="animate-spin" />}
                Turn on
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setSetup(undefined); setError(undefined) }}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </Block>
    )
  }

  if (!enabled) {
    return (
      <Block title="Two-factor authentication" description="A code from an authenticator app at sign-in, on top of your password. Recommended.">
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault()
            const data = await run('start', () => authClient.twoFactor.enable({ password: password(e.currentTarget) }))
            if (data?.method === 'totp') setSetup({ totpURI: data.totpURI, backupCodes: data.backupCodes })
          }}
        >
          {error && <FormAlert>{error}</FormAlert>}
          <Field label="Confirm your password" name="password" type="password" autoComplete="current-password" required />
          <Button type="submit" disabled={pending === 'start'}>
            {pending === 'start' && <Loader2 className="animate-spin" />}
            Set up two-factor
          </Button>
        </form>
      </Block>
    )
  }

  return (
    <Block title="Two-factor authentication" description="On. Sign-ins ask for a code from your authenticator app.">
      <div className="space-y-5">
        <p className="text-status-success-text flex items-center gap-2 text-sm font-medium">
          <ShieldCheck className="size-4" /> Protected with an authenticator app
        </p>
        {codes && <BackupCodes codes={codes} fresh />}
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault()
            const form = e.currentTarget
            const action = (e.nativeEvent as SubmitEvent).submitter?.getAttribute('value')
            if (action === 'codes') {
              const data = await run('codes', () => authClient.twoFactor.generateBackupCodes({ password: password(form) }))
              if (data) {
                setCodes(data.backupCodes)
                form.reset()
              }
              return
            }
            const ok = await run('disable', () => authClient.twoFactor.disable({ password: password(form) }))
            if (!ok) return
            setCodes(undefined)
            await queryClient.invalidateQueries({ queryKey: ['session'] })
            toast.success('Two-factor authentication is off.')
          }}
        >
          {error && <FormAlert>{error}</FormAlert>}
          <Field label="Confirm your password" name="password" type="password" autoComplete="current-password" required hint="Needed to change these settings." />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="outline" value="codes" disabled={!!pending}>
              {pending === 'codes' && <Loader2 className="animate-spin" />}
              New backup codes
            </Button>
            <Button type="submit" variant="outline" value="disable" disabled={!!pending} className="text-status-danger-text">
              {pending === 'disable' && <Loader2 className="animate-spin" />}
              Turn off
            </Button>
          </div>
        </form>
      </div>
    </Block>
  )
}

/** The one-time list of backup codes, with a copy button. */
function BackupCodes({ codes, fresh }: { codes: string[]; fresh?: boolean }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium">{fresh ? 'Your new backup codes' : 'Backup codes'}</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(codes.join('\n'))
              toast.success('Backup codes copied')
            } catch {
              toast.error("Couldn't copy. Select the codes and copy them by hand.")
            }
          }}
        >
          <Copy /> Copy
        </Button>
      </div>
      <ul className="bg-muted grid grid-cols-2 gap-x-6 gap-y-1 rounded-md px-3 py-2.5 font-mono text-[13px] sm:grid-cols-3">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <p className="text-muted-foreground text-[13px]">
        Keep these somewhere safe. If you lose your phone, each one signs you in once.{fresh ? ' The old codes no longer work.' : ''}
      </p>
    </div>
  )
}

function PasswordBlock() {
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)
    const next = passwordSchema.safeParse(data.get('next'))
    if (!next.success) return setError(next.error.issues[0]?.message)
    setPending(true)
    setError(undefined)
    const { error } = await authClient.changePassword({
      currentPassword: String(data.get('current')),
      newPassword: next.data,
      revokeOtherSessions: true,
    })
    setPending(false)
    if (error) return setError(authErrorMessage(error))
    form.reset()
    toast.success('Password changed. Other devices were signed out.')
  }
  return (
    <Block title="Password" description="Changing it signs you out everywhere else.">
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <FormAlert>{error}</FormAlert>}
        <Field label="Current password" name="current" type="password" autoComplete="current-password" />
        <Field label="New password" name="next" type="password" autoComplete="new-password" hint="At least 10 characters." />
        <Button type="submit" disabled={pending}>
          {pending && <Loader2 className="animate-spin" />}
          Change password
        </Button>
      </form>
    </Block>
  )
}

function LinkedAccounts() {
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const { data, error } = await authClient.listAccounts()
      if (error) throw error
      return data
    },
  })
  const discord = accounts.data?.find((a) => a.providerId === 'discord')
  return (
    <Block title="Sign-in methods" description="Link Discord to sign in with one click.">
      <div className="flex items-center gap-3 rounded-lg border px-4 py-3">
        <DiscordIcon className="size-5 text-[#5865F2]" />
        <div className="flex-1 text-sm">
          <p className="font-medium">Discord</p>
          <p className="text-muted-foreground text-[13px]">{discord ? 'Linked' : 'Not linked'}</p>
        </div>
        {!discord && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const { error } = await authClient.linkSocial({ provider: 'discord', callbackURL: '/settings?tab=security' })
              if (error) toast.error(error.status === 404 ? "Discord sign-in isn't available yet." : authErrorMessage(error))
            }}
          >
            Link
          </Button>
        )}
      </div>
    </Block>
  )
}

function Sessions() {
  const current = useQuery(sessionQuery).data?.session
  const queryClient = useQueryClient()
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => {
      const { data, error } = await authClient.listSessions()
      if (error) throw error
      return data
    },
  })
  const revokeOthers = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.revokeOtherSessions()
      if (error) throw new Error(authErrorMessage(error))
    },
    onSuccess: () => {
      toast.success('Signed out of other devices')
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
    onError: (e) => toast.error(e.message),
  })
  return (
    <Block title="Devices" description="Where you're signed in right now.">
      {!sessions.data ? (
        <Skeleton className="h-24" />
      ) : (
        <div className="space-y-4">
          <ul className="divide-y rounded-lg border">
            {sessions.data.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                {/mobile|iphone|android|expo/i.test(s.userAgent ?? '') ? (
                  <MonitorSmartphone className="text-muted-foreground size-4" />
                ) : (
                  <Laptop className="text-muted-foreground size-4" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{describeAgent(s.userAgent)}</p>
                  <p className="text-muted-foreground text-[13px]">
                    {s.id === current?.id ? 'This device' : `Active ${ago(new Date(s.updatedAt).toISOString())}`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          {sessions.data.length > 1 && (
            <Button variant="outline" onClick={() => revokeOthers.mutate()} disabled={revokeOthers.isPending}>
              Sign out other devices
            </Button>
          )}
        </div>
      )}
    </Block>
  )
}

function describeAgent(ua?: string | null) {
  if (!ua) return 'Unknown device'
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : /Expo|okhttp|CFNetwork/.test(ua) ? 'Nebueye app' : 'Browser'
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : ''
  return os ? `${browser} on ${os}` : browser
}

// --------------------------------------------------------------- workspace

function useActiveWorkspace() {
  const session = useQuery(sessionQuery).data
  const workspaces = useQuery(workspacesQuery).data ?? []
  return workspaces.find((w) => w.id === session?.session.activeOrganizationId)
}

function useMyRole() {
  const org = useActiveWorkspace()
  return useQuery({
    queryKey: ['my-role', org?.id],
    enabled: !!org,
    queryFn: async () => {
      const { data, error } = await authClient.organization.getActiveMemberRole()
      if (error) throw error
      return data.role
    },
  }).data
}

function Workspace() {
  const { create } = Route.useSearch()
  const org = useActiveWorkspace()
  const role = useMyRole()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState(org?.name ?? '')
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string>()
  const canEdit = role === 'owner' || role === 'admin'
  const renameCheck = useVaultAvailability(canEdit ? name : '', org?.slug)
  const createCheck = useVaultAvailability(newName)

  const rename = useMutation({
    mutationFn: async () => {
      const parsed = vaultNameSchema.safeParse(name)
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message)
      const { error } = await authClient.organization.update({ data: { name: parsed.data, slug: vaultSlug(parsed.data) } })
      if (error) throw new Error(authErrorMessage(error))
    },
    onSuccess: () => {
      toast.success('Vault renamed')
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
    },
    onError: (e) => toast.error(e.message),
  })

  const createWs = useMutation({
    mutationFn: async () => {
      const parsed = vaultNameSchema.safeParse(newName)
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message)
      const { error } = await authClient.organization.create({ name: parsed.data, slug: vaultSlug(parsed.data) })
      if (error) throw new Error(authErrorMessage(error))
    },
    onSuccess: async () => {
      toast.success('Vault created')
      await queryClient.invalidateQueries()
      navigate({ to: '/' })
    },
    onError: (e) => setError(e.message),
  })

  const leave = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.organization.leave({ organizationId: org!.id })
      if (error) throw new Error(authErrorMessage(error))
    },
    onSuccess: async () => {
      toast.success('You left the Vault')
      await queryClient.invalidateQueries()
      navigate({ to: '/' })
    },
    onError: (e) => toast.error(e.message),
  })

  if (!org) return null
  return (
    <div>
      <Block title="Vault" description={canEdit ? 'The name everyone in this Vault sees. It has to be unique on Nebueye.' : 'Only owners and admins can rename it.'}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            rename.mutate()
          }}
          className="space-y-4"
        >
          <div className="flex items-center gap-3">
            <WorkspaceInitial name={name || org.name} />
            <span className="text-muted-foreground text-[13px]">Created {ago(new Date(org.createdAt).toISOString())}</span>
          </div>
          <Field
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canEdit}
            maxLength={VAULT_NAME_MAX + 8}
            error={renameCheck.state === 'taken' || renameCheck.state === 'invalid' ? renameCheck.message : undefined}
            hint={canEdit ? VAULT_NAME_RULE : undefined}
          />
          {canEdit && (
            <Button type="submit" disabled={rename.isPending || name.trim() === org.name || renameCheck.state === 'taken' || renameCheck.state === 'invalid'}>
              Save
            </Button>
          )}
        </form>
      </Block>

      <Block title="New Vault" description="Keep separate operations apart, each with its own orders and members.">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            createWs.mutate()
          }}
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
        >
          <Field
            label="Vault name"
            className="flex-1"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={VAULT_NAME_MAX + 8}
            autoFocus={create}
            error={error ?? (createCheck.state === 'taken' || createCheck.state === 'invalid' ? createCheck.message : undefined)}
            hint={createCheck.state === 'free' ? `${createCheck.slug} is free` : VAULT_NAME_RULE}
          />
          <Button type="submit" variant="outline" className="h-10" disabled={createWs.isPending || !newName.trim() || createCheck.state === 'taken' || createCheck.state === 'invalid'}>
            Create
          </Button>
        </form>
      </Block>

      {canEdit && <DeleteWorkspaceData name={org.name} />}

      {role !== 'owner' && (
        <Block title="Leave Vault" description="You'll lose access to its orders until someone invites you back.">
          <Button
            variant="outline"
            className="text-status-danger-text"
            disabled={leave.isPending}
            onClick={() => confirm(`Leave ${org.name}?`) && leave.mutate()}
          >
            Leave {org.name}
          </Button>
        </Block>
      )}
    </div>
  )
}

/** Typing the workspace's name unlocks the button; the API checks it again. */
function DeleteWorkspaceData({ name }: { name: string }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const matches = typed.trim().toLowerCase() === name.trim().toLowerCase()
  const close = () => {
    setOpen(false)
    setTyped('')
  }
  const wipe = useMutation({
    mutationFn: () =>
      apiFetch<WorkspaceDataDeleted>('/workspace/delete-data', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ confirm: typed }),
      }),
    onSuccess: async (d) => {
      toast.success(`Deleted ${plural(d.orders, 'order')}, ${plural(d.accounts, 'email account')} and ${plural(d.alerts, 'alert')}`)
      close()
      await queryClient.invalidateQueries()
    },
    onError: (e) => toast.error(e.message),
  })

  return (
    <Block
      title="Delete Vault data"
      description="Removes every order, email account and alert stored in this Vault and disconnects its desktops. Members stay, and nothing is deleted from the desktop apps."
    >
      {!open ? (
        <Button variant="outline" className="text-status-danger-text" onClick={() => setOpen(true)}>
          <Trash2 />
          Delete all data…
        </Button>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (matches) wipe.mutate()
          }}
        >
          <Field
            label={`Type “${name}” to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            hint="This can't be undone here. To bring the data back, connect a desktop again and turn on Cloud Sync."
          />
          <div className="flex gap-2">
            <Button type="submit" variant="destructive" disabled={!matches || wipe.isPending}>
              {wipe.isPending && <Loader2 className="animate-spin" />}
              Delete everything
            </Button>
            <Button type="button" variant="ghost" onClick={close} disabled={wipe.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Block>
  )
}

// ----------------------------------------------------------------- members

function Members() {
  const org = useActiveWorkspace()
  const role = useMyRole()
  const me = useQuery(sessionQuery).data?.user
  const queryClient = useQueryClient()
  const canManage = role === 'owner' || role === 'admin'
  const full = useQuery({
    queryKey: ['org-full', org?.id],
    enabled: !!org,
    queryFn: async () => {
      const { data, error } = await authClient.organization.getFullOrganization()
      if (error) throw error
      return data
    },
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['org-full'] })

  const [email, setEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member')
  const invite = useMutation({
    mutationFn: async () => {
      const parsed = z.email('Enter a valid email address').safeParse(email.trim())
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message)
      const { error } = await authClient.organization.inviteMember({ email: parsed.data, role: inviteRole })
      if (error) throw new Error(authErrorMessage(error))
    },
    onSuccess: () => {
      toast.success(`Invitation sent to ${email.trim()}`)
      setEmail('')
      refresh()
    },
    onError: (e) => toast.error(e.message),
  })

  const pending = full.data?.invitations.filter((i) => i.status === 'pending') ?? []

  return (
    <div>
      {canManage && (
        <Block title="Invite people" description="They'll get an email link to join this Vault. Invites last 7 days.">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              invite.mutate()
            }}
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
          >
            <Field label="Email" type="email" className="flex-1" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
            <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as 'member' | 'admin')}>
              <SelectTrigger className="h-10! w-full sm:w-32" aria-label="Role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" className="h-10" disabled={invite.isPending || !email.trim()}>
              {invite.isPending && <Loader2 className="animate-spin" />}
              Send invite
            </Button>
          </form>
        </Block>
      )}

      <Block title="Members" description="Owners and admins can invite and remove people. Members can view everything.">
        {!full.data ? (
          <Skeleton className="h-32" />
        ) : (
          <ul className="divide-y rounded-lg border">
            {full.data.members.map((m) => (
              <li key={m.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <Avatar className="size-8">
                  {m.user.image && <AvatarImage src={m.user.image} alt="" />}
                  <AvatarFallback className="bg-accent text-accent-foreground text-[11px] font-semibold">{initials(m.user.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {m.user.name} {m.user.id === me?.id && <span className="text-subtle font-normal">(you)</span>}
                  </p>
                  <p className="text-muted-foreground truncate text-[13px]">{m.user.email}</p>
                </div>
                <span className="text-muted-foreground text-[13px] capitalize">{m.role}</span>
                {canManage && m.role !== 'owner' && m.user.id !== me?.id && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${m.user.name}`}
                    onClick={async () => {
                      if (!confirm(`Remove ${m.user.name} from this Vault?`)) return
                      const { error } = await authClient.organization.removeMember({ memberIdOrEmail: m.id })
                      if (error) return toast.error(authErrorMessage(error))
                      toast.success(`${m.user.name} removed`)
                      refresh()
                    }}
                  >
                    <Trash2 />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Block>

      {pending.length > 0 && (
        <Block title="Pending invitations">
          <ul className="divide-y rounded-lg border">
            {pending.map((i) => (
              <li key={i.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <AtSign className="text-muted-foreground size-4" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{i.email}</p>
                  <p className="text-muted-foreground text-[13px]">
                    <span className="capitalize">{i.role}</span> · expires {ago(new Date(i.expiresAt).toISOString())}
                  </p>
                </div>
                {canManage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      const { error } = await authClient.organization.cancelInvitation({ invitationId: i.id })
                      if (error) return toast.error(authErrorMessage(error))
                      refresh()
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Block>
      )}
    </div>
  )
}

// ------------------------------------------------------------ integrations

const SOURCES = [
  {
    icon: Inbox,
    title: 'Forward your order emails',
    body: 'Set one forwarding rule in Gmail, Outlook or iCloud and every confirmation, shipment and cancel lands here automatically.',
  },
  {
    icon: AtSign,
    title: 'Connect an inbox',
    body: 'Link an IMAP mailbox for a full sync, including your history from before you joined.',
  },
  {
    icon: Store,
    title: 'Marketplaces',
    body: 'List delivered items on eBay and other marketplaces, and track what they sell for.',
  },
]

function DesktopDevices() {
  const org = useActiveWorkspace()
  const queryClient = useQueryClient()
  const q = useQuery({
    queryKey: ['devices', org?.id],
    enabled: !!org,
    queryFn: () => apiFetch<{ devices: DeviceInfo[] }>('/devices').then((r) => r.devices),
    refetchInterval: 15_000,
  })
  const disconnect = useMutation({
    mutationFn: (id: string) => apiFetch(`/devices/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Desktop disconnected')
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (e) => toast.error(e.message),
  })

  return (
    <Block
      title="Nebueye desktop"
      description="Sign in from the desktop app (Settings → Cloud) and turn on Cloud Sync to see its orders here."
    >
      {!q.data ? (
        <Skeleton className="h-16" />
      ) : q.data.length === 0 ? (
        <div className="text-muted-foreground rounded-lg border border-dashed px-4 py-5 text-sm">
          No desktops connected yet. Have a code from the desktop app?{' '}
          <Link to="/device" className="text-primary font-medium hover:underline">
            Enter it here
          </Link>
          .
        </div>
      ) : (
        <ul className="divide-y rounded-lg border">
          {q.data.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-4 py-3 text-sm">
              <span className="bg-accent text-accent-foreground grid size-9 shrink-0 place-items-center rounded-lg">
                <Laptop className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{d.name}</p>
                <p className="text-muted-foreground tabular truncate text-[13px]">
                  {d.lastSyncAt ? `Synced ${ago(d.lastSyncAt)} · ${plural(d.orders, 'order')}` : 'Connected, sync is off'}
                  {' · '}
                  {d.userName}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={disconnect.isPending}
                onClick={() => confirm(`Disconnect ${d.name}? It will stop syncing until someone signs in again. Orders already uploaded stay.`) && disconnect.mutate(d.id)}
              >
                Disconnect
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Block>
  )
}

function Integrations() {
  return (
    <div className="space-y-6">
      <DesktopDevices />
      <p className="text-muted-foreground max-w-2xl text-[15px] leading-6">
        Nebueye reads the emails retailers send you. These are the ways your data will get in, and where it'll go next.
        They're on the way.
      </p>
      <ul className="grid gap-px overflow-hidden rounded-xl border bg-[var(--border)] sm:grid-cols-2">
        {SOURCES.map((s) => (
          <li key={s.title} className="bg-card space-y-2 p-6">
            <div className="flex items-center justify-between">
              <s.icon className="text-primary size-5" />
              <span className="text-subtle bg-surface-sunken rounded-full px-2 py-0.5 text-[11px] font-medium tracking-wide uppercase">
                Coming soon
              </span>
            </div>
            <h3 className="pt-2 font-semibold">{s.title}</h3>
            <p className="text-muted-foreground text-[14px] leading-6">{s.body}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}
